import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import { EmissionPointPicker } from '../../../components/bookkeeping/EmissionPointPicker.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type {
  Account,
  AccountNode,
  TaxRate,
  Vendor,
  WithholdingRate,
} from '../../../types/bookkeeping.js';

const QTY_SCALE = 1_000n;
const TAX_DIVISOR = 100_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const out: Account[] = [];
  const walk = (ns: AccountNode[]) => {
    for (const n of ns) {
      out.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

interface LineForm {
  description: string;
  quantity: string; // ×1000
  unit_price: string; // cents
  tax_rate_id: string;
  account_id: string;
}

function defaultLine(): LineForm {
  return { description: '', quantity: '1000', unit_price: '0', tax_rate_id: '', account_id: '' };
}

interface WithholdingForm {
  withholding_rate_id: string;
  base: string; // cents
}

interface LCDetail {
  id: string;
  number: string;
  vendor_id: string;
  issue_date: string;
  currency: string;
  subtotal: string;
  tax_total: string;
  withholding_total: string;
  grand_total: string;
  sri_status: string | null;
  lines: Array<{ description: string | null; line_subtotal: string; tax_amount: string }>;
  withholdings: Array<{ base: string; amount: string }>;
}

const fmtMoney = (cents: string | bigint): string => {
  const n = typeof cents === 'bigint' ? cents : BigInt(cents || '0');
  return `${n / 100n}.${String(n % 100n).padStart(2, '0')}`;
};

export function LiquidacionCompraPage() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: lcId } = useParams<{ id: string }>();

  const [vendorId, setVendorId] = useState('');
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [emissionPointId, setEmissionPointId] = useState<string | null>(null);
  const [lines, setLines] = useState<LineForm[]>([defaultLine()]);
  const [withholdings, setWithholdings] = useState<WithholdingForm[]>([]);
  const [error, setError] = useState<string | null>(null);

  // ── Detail mode ──────────────────────────────────────────────
  const detailQ = useQuery({
    queryKey: ['bk-liquidacion-compra', lcId],
    queryFn: () => api<LCDetail>(`/bookkeeping/purchases/liquidacion-compra/${lcId}`),
    enabled: !!activeOrgId && !!lcId,
  });

  // ── Create-mode reference data ───────────────────────────────
  const { data: vendorsData } = useQuery({
    queryKey: ['bookkeeping-vendors', activeOrgId],
    queryFn: () => api<{ vendors: Vendor[] }>('/bookkeeping/vendors?limit=200'),
    enabled: !!activeOrgId && !lcId,
  });
  const { data: taxRatesData } = useQuery({
    queryKey: ['bookkeeping-tax-rates', activeOrgId],
    queryFn: () => api<{ tax_rates: TaxRate[] }>('/bookkeeping/sales/tax-rates?limit=100'),
    enabled: !!activeOrgId && !lcId,
  });
  const { data: whRatesData } = useQuery({
    queryKey: ['bookkeeping-withholding-rates', activeOrgId],
    queryFn: () =>
      api<{ withholding_rates: WithholdingRate[] }>(
        '/bookkeeping/sales/withholding-rates?limit=100',
      ),
    enabled: !!activeOrgId && !lcId,
  });
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId && !lcId,
  });
  const vendors = vendorsData?.vendors ?? [];
  const taxRates = taxRatesData?.tax_rates ?? [];
  const whRates = (whRatesData?.withholding_rates ?? []).filter((w) => w.account_id_payable);
  const accounts = flattenTree(accountsData?.tree ?? []);

  const totals = lines.reduce(
    (acc, l) => {
      const qty = BigInt(l.quantity || '0');
      const price = BigInt(l.unit_price || '0');
      const lineSubtotal = (qty * price) / QTY_SCALE;
      const tr = taxRates.find((r) => r.id === l.tax_rate_id);
      const pct = tr ? BigInt(tr.percentage) : 0n;
      acc.subtotal += lineSubtotal;
      acc.iva += (lineSubtotal * pct) / TAX_DIVISOR;
      return acc;
    },
    { subtotal: 0n, iva: 0n },
  );
  const withheld = withholdings.reduce((sum, w) => {
    const rate = whRates.find((r) => r.id === w.withholding_rate_id);
    if (!rate) return sum;
    return sum + (BigInt(w.base || '0') * BigInt(rate.percentage)) / TAX_DIVISOR;
  }, 0n);
  const grandTotal = totals.subtotal + totals.iva;
  const neto = grandTotal - withheld;

  const createMut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        vendor_id: vendorId,
        issue_date: issueDate,
        currency: 'USD',
        emission_point_id: emissionPointId ?? undefined,
        lines: lines.map((l) => ({
          description: l.description || null,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_rate_id: l.tax_rate_id,
          account_id: l.account_id,
        })),
        withholdings: withholdings
          .filter((w) => w.withholding_rate_id && BigInt(w.base || '0') > 0n)
          .map((w) => ({ withholding_rate_id: w.withholding_rate_id, base: w.base })),
      });
      return api<{ id: string }>('/bookkeeping/purchases/liquidacion-compra', {
        method: 'POST',
        body,
      });
    },
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['bk-liquidaciones-compra'] });
      nav(`/app/bookkeeping/purchases/liquidacion-compra/${created.id}`);
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  function submit() {
    if (!vendorId) return setError('vendor_required');
    if (lines.length === 0 || lines.some((l) => !l.tax_rate_id || !l.account_id))
      return setError('no_lines');
    setError(null);
    createMut.mutate();
  }

  // ── Render: detail mode ──────────────────────────────────────
  if (lcId) {
    const lc = detailQ.data;
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">
          {t('liquidacionCompra.title')} {lc ? lc.number : ''}
        </h1>
        {lc && (
          <>
            <div className="rounded border bg-white p-4 text-sm">
              <div className="space-y-1">
                <div>
                  {t('liquidacionCompra.subtotal')}:{' '}
                  <span className="font-mono">{fmtMoney(lc.subtotal)}</span>
                </div>
                <div>
                  {t('liquidacionCompra.iva')}:{' '}
                  <span className="font-mono">{fmtMoney(lc.tax_total)}</span>
                </div>
                <div>
                  {t('liquidacionCompra.retenido')}:{' '}
                  <span className="font-mono">{fmtMoney(lc.withholding_total)}</span>
                </div>
                <div className="font-semibold">
                  {t('liquidacionCompra.neto')}:{' '}
                  <span className="font-mono">
                    {fmtMoney(BigInt(lc.grand_total) - BigInt(lc.withholding_total))} {lc.currency}
                  </span>
                </div>
              </div>
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-2 py-1">{t('liquidacionCompra.description')}</th>
                    <th className="px-2 py-1">{t('liquidacionCompra.subtotal')}</th>
                    <th className="px-2 py-1">{t('liquidacionCompra.iva')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lc.lines.map((l, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable order
                    <tr key={i} className="border-b">
                      <td className="px-2 py-1">{l.description ?? ''}</td>
                      <td className="px-2 py-1 font-mono">{fmtMoney(l.line_subtotal)}</td>
                      <td className="px-2 py-1 font-mono">{fmtMoney(l.tax_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <SriStatusTrack docType="liquidacion-compra" docId={lc.id} />
          </>
        )}
      </div>
    );
  }

  // ── Render: create mode ──────────────────────────────────────
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('liquidacionCompra.create')}</h1>

      <div className="grid grid-cols-2 gap-3 rounded border bg-white p-4 text-sm">
        <label>
          {t('liquidacionCompra.vendor')}
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.display_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Fecha
          <input
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <EmissionPointPicker value={emissionPointId} onChange={setEmissionPointId} />
      </div>

      <div className="rounded border bg-white p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="px-2 py-1">{t('liquidacionCompra.description')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.quantity')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.unit_price')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.tax_rate')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.account')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: stable row order
              <tr key={i} className="border-b">
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.description}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
                      )
                    }
                    className="w-full rounded border px-2 py-1"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.quantity}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)),
                      )
                    }
                    className="w-20 rounded border px-2 py-1 text-right font-mono"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.unit_price}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)),
                      )
                    }
                    className="w-24 rounded border px-2 py-1 text-right font-mono"
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={l.tax_rate_id}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, tax_rate_id: e.target.value } : x)),
                      )
                    }
                    className="rounded border px-2 py-1"
                  >
                    <option value="">—</option>
                    {taxRates.map((tr) => (
                      <option key={tr.id} value={tr.id}>
                        {tr.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <select
                    value={l.account_id}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, account_id: e.target.value } : x)),
                      )
                    }
                    className="rounded border px-2 py-1"
                  >
                    <option value="">—</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    className="text-red-600 hover:underline"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => setLines((ls) => [...ls, defaultLine()])}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {t('liquidacionCompra.add_line')}
        </button>
      </div>

      <div className="rounded border bg-white p-4">
        <h2 className="mb-2 text-sm font-semibold">{t('liquidacionCompra.withholdings')}</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="px-2 py-1">{t('liquidacionCompra.withholding_rate')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.base')}</th>
              <th className="px-2 py-1">{t('liquidacionCompra.amount')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {withholdings.map((w, i) => {
              const rate = whRates.find((r) => r.id === w.withholding_rate_id);
              const amount = rate
                ? (BigInt(w.base || '0') * BigInt(rate.percentage)) / TAX_DIVISOR
                : 0n;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: stable row order
                <tr key={i} className="border-b">
                  <td className="px-2 py-1">
                    <select
                      value={w.withholding_rate_id}
                      onChange={(e) =>
                        setWithholdings((ws) =>
                          ws.map((x, j) =>
                            j === i ? { ...x, withholding_rate_id: e.target.value } : x,
                          ),
                        )
                      }
                      className="rounded border px-2 py-1"
                    >
                      <option value="">—</option>
                      {whRates.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.code} {r.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={w.base}
                      onChange={(e) =>
                        setWithholdings((ws) =>
                          ws.map((x, j) => (j === i ? { ...x, base: e.target.value } : x)),
                        )
                      }
                      className="w-28 rounded border px-2 py-1 text-right font-mono"
                    />
                  </td>
                  <td className="px-2 py-1 font-mono">{fmtMoney(amount)}</td>
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => setWithholdings((ws) => ws.filter((_, j) => j !== i))}
                      className="text-red-600 hover:underline"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() =>
            setWithholdings((ws) => [
              ...ws,
              { withholding_rate_id: '', base: totals.subtotal.toString() },
            ])
          }
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {t('liquidacionCompra.add_withholding')}
        </button>
      </div>

      <div className="flex items-center justify-between rounded border bg-white p-4 text-sm">
        <div className="space-y-1">
          <div>
            {t('liquidacionCompra.subtotal')}:{' '}
            <span className="font-mono">{fmtMoney(totals.subtotal)}</span>
          </div>
          <div>
            {t('liquidacionCompra.iva')}: <span className="font-mono">{fmtMoney(totals.iva)}</span>
          </div>
          <div>
            {t('liquidacionCompra.retenido')}:{' '}
            <span className="font-mono">{fmtMoney(withheld)}</span>
          </div>
          <div className="font-semibold">
            {t('liquidacionCompra.neto')}: <span className="font-mono">{fmtMoney(neto)}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={createMut.isPending}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          {t('liquidacionCompra.create')}
        </button>
      </div>
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {t(`liquidacionCompra.errors.${error}`, { defaultValue: error })}
        </p>
      )}
    </div>
  );
}
